import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { Svg, Path } from 'react-native-svg';
import { useTranslation } from 'react-i18next';
import { FONTS } from '../constants/config';

interface TargetAchievementCardProps {
  title: string;
  achieved: number | string;
  target: number | string;
  unit?: string; // e.g., "عدد", "نسبة", "%"
  onViewDetails?: () => void;
  achievedColor?: string; // Override achievement color
}

const ChevronLeftIcon = () => (
  <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <Path
      d="M10 12L6 8L10 4"
      stroke="#414042"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

export default function TargetAchievementCard({
  title,
  achieved,
  target,
  unit = 'عدد',
  onViewDetails,
  achievedColor = '#2ca02c', // Green color from Figma
}: TargetAchievementCardProps) {
  const { t } = useTranslation();

  return (
    <View style={styles.card}>
      {/* Title */}
      <View style={styles.titleContainer}>
        <Text style={styles.title}>
          {title}
        </Text>
      </View>

      {/* Target and Achievement sections */}
      <View style={styles.metricsContainer}>
        {/* Target section - Display first */}
        <View style={[styles.metricBox, { flex: 1 }]}>
          <Text style={styles.metricLabel}>
            {t('common.target') || 'المستهدف'}
          </Text>
          <View style={styles.metricValueContainer}>
            <Text style={styles.metricUnit}>
              {unit}
            </Text>
            <Text style={styles.metricValue}>
              {target}
            </Text>
          </View>
        </View>

        {/* Achieved section - Display second */}
        <View style={[styles.metricBox, { flex: 1 }]}>
          <Text style={styles.metricLabel}>
            {t('common.achieved') || 'المحقق'}
          </Text>
          <View style={styles.metricValueContainer}>
            <Text style={styles.metricUnit}>
              {unit}
            </Text>
            <Text style={[styles.metricValue, { color: achievedColor }]}>
              {achieved}
            </Text>
          </View>
        </View>
      </View>

      {/* View Details button */}
      {onViewDetails && (
        <TouchableOpacity
          style={styles.viewDetailsButton}
          onPress={onViewDetails}
          activeOpacity={0.7}
        >
          <ChevronLeftIcon />
          <Text style={styles.viewDetailsText}>
            {t('common.viewDetails') || 'عرض التفاصيل'}
          </Text>
        </TouchableOpacity>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    padding: 12,
    gap: 8,
  },
  titleContainer: {
    width: '100%',
  },
  title: {
    fontFamily: FONTS.regular,
    fontSize: 14,
    lineHeight: 19.6,
    color: '#414042',
    width: '100%',
    textAlign: 'right',
    writingDirection: 'rtl',
  },
  metricsContainer: {
    flexDirection: 'row',
    gap: 8,
    width: '100%',
  },
  metricBox: {
    backgroundColor: '#F1F5F9',
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 2,
    gap: 4,
    alignItems: 'flex-end',
  },
  metricLabel: {
    fontFamily: FONTS.regular,
    fontSize: 12,
    lineHeight: 13.57,
    color: '#414042',
    width: '100%',
    textAlign: 'right',
    writingDirection: 'rtl',
  },
  metricValueContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  metricUnit: {
    fontFamily: FONTS.regular,
    fontSize: 12,
    lineHeight: 13.57,
    color: '#414042',
    textAlign: 'right',
    writingDirection: 'rtl',
  },
  metricValue: {
    fontFamily: FONTS.medium,
    fontSize: 14,
    lineHeight: 14,
    textAlign: 'right',
  },
  viewDetailsButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 8,
    paddingHorizontal: 8,
    paddingVertical: 3,
    minHeight: 24,
    borderRadius: 4,
  },
  viewDetailsText: {
    fontFamily: FONTS.regular,
    fontSize: 12,
    lineHeight: 13.57,
    color: '#414042',
    textAlign: 'right',
    writingDirection: 'rtl',
  },
});
